import { Fragment, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Grid, Typography, Divider, Button, FormControlLabel, Checkbox } from '@material-ui/core';
import NumberFormat from 'react-number-format';
import styled from 'styled-components';

import { modalsHide, modalsToggle } from 'actions';
import ConfirmModal from 'components/Modals/ConfirmModal';
import { USER_STATUSES, ADMIN_JOB_TITLES, DATE_FORMAT } from 'constants';
import { SendOfferModal } from 'components/Offers';
import moment from 'moment';
import { useViewedVehicle } from '../../../../hooks/useViewedVehicle';
import { useCurrentUser } from '../../../../../../../hooks/selectors';
import {
  useGetBuyerFeeQuery,
  useRetryFeeChargeMutation,
  useUnawardVehicleMutation
} from '../../../../../../../services/api/vehicle-actions';
import { useUnawardWithCounterMutation } from '../../../../../../../services/api/offer-auction';
import { ActionDivider } from '../../../UI';
import { InfoBlock } from '../../../InfoBlock';
import toast from 'react-hot-toast';
import { UnawardFeeDialog, UnawardFeeEntry } from './UnawardFeeDialog';

const modalUnawardTitle = 'unawardVehicleModal';
const modalTitlesPage = 'unawardTitlesPageModal'

const chargeModalTitle = 'retryChargeBuyer';

const StyledDescription = styled(Typography)`
  && {
    color: #616161;
    margin-top: 8px;
  }
`;

const StyledText = styled.span`
  && {
    color: #212121;
  }
`;

const StyledSpan = styled(Typography)`
  && {
    text-decoration: underline;
    cursor: pointer;
    font-weight: 500;
  }
`;

const StyledButton = styled(Button)`
  && {
    margin-top: 16px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 20px;
`;

export const UnawardBlock = () => {
  const currentVehicle = useViewedVehicle();
  const currentUser = useCurrentUser();
  const [retryTransaction] = useRetryFeeChargeMutation();
  const [unawardVehicle] = useUnawardVehicleMutation();
  const [unawardWithCounter] = useUnawardWithCounterMutation();
  const [removeFromTitles, setRemoveFromTitles] = useState(false);
  const [counterValues, setCounterValues] = useState<{ id: number; amount: number; notes: string } | null>(null);
  const [feeDialogOpen, setFeeDialogOpen] = useState(false);
  const dispatch = useDispatch();

  const { data: fee } = useGetBuyerFeeQuery(currentVehicle?.price || 0, {
    skip: !currentVehicle || !currentVehicle.price
  });

  if (!currentVehicle) return null;

  const handleClickUnaward = () => {
    const isTimeEnded = currentVehicle.date_end && moment.utc(currentVehicle.date_end).isBefore(moment.utc());
    if (!isTimeEnded) {
      dispatch(modalsToggle(modalUnawardTitle));
    } else {
      dispatch(modalsToggle(`unaward-${currentVehicle.id}`));
    }
  };

  const handleConfirmUnaward = () => {
    dispatch(modalsToggle(modalUnawardTitle));
    dispatch(modalsToggle(modalTitlesPage));
  };

  const handleCounterSuccess = (values: { id: number; amount: number; notes: string }) => {
    setCounterValues(values);
    dispatch(modalsHide(`unaward-${currentVehicle.id}`));
    dispatch(modalsToggle(modalTitlesPage));
  };

  const handleTitlesConfirm = () => {
    dispatch(modalsHide(modalTitlesPage));
    setFeeDialogOpen(true);
  };

  const handleUnaward = async (buyerFee?: UnawardFeeEntry | null, sellerFee?: UnawardFeeEntry | null) => {
    const feeArgs = {
      ...(buyerFee && { buyer_miscellaneous_fees: { type: buyerFee.description, amount: buyerFee.amount } }),
      ...(sellerFee && { seller_miscellaneous_fees: { type: sellerFee.description, amount: sellerFee.amount } })
    };
    let response;
    if (counterValues) {
      response = await unawardWithCounter({
        vehicleId: counterValues.id,
        amount: counterValues.amount,
        notes: counterValues.notes,
        remove_from_titles: removeFromTitles || undefined,
        ...feeArgs
      });
    } else {
      response = await unawardVehicle({
        vehicleId: currentVehicle.id,
        remove_from_titles: removeFromTitles || undefined,
        ...feeArgs
      });
    }
    if (!('error' in response)) {
      toast.success(removeFromTitles ? 'Title was removed' : 'Title was Unwinded');
    }
    setRemoveFromTitles(false);
    setCounterValues(null);
    setFeeDialogOpen(false);
  };

  const toggleChargeBuyerModal = () => dispatch(modalsToggle(chargeModalTitle));

  const chargeBuyer = async () => {
    const response = await retryTransaction({
      userId: currentVehicle.awarded_user!.id,
      vehicleId: currentVehicle.id,
      auction_id: currentVehicle.auction_id,
      amount: fee
    });

    if ('error' in response) {
      toast.error((response.error as any)?.data?.message || 'Failed to charge buyer');
    } else {
      toast.success('Buyer recharged');
    }

    dispatch(modalsHide(chargeModalTitle));
  };

  return (
    <InfoBlock>
      <Wrapper>
        {![ADMIN_JOB_TITLES.SALESMAN, ADMIN_JOB_TITLES.LISTER].includes(currentUser.job_title) && (
          <StyledSpan onClick={handleClickUnaward}>Unaward vehicle</StyledSpan>
        )}
        <ConfirmModal
          modalId={modalUnawardTitle}
          title="Are you sure you want to unaward this vehicle?"
          handleSubmit={handleConfirmUnaward}
          submitText="Unaward"
        />
        <ConfirmModal
          modalId={modalTitlesPage}
          title="The vehicle is shown on the Title's page."
          handleSubmit={handleTitlesConfirm}
          submitText="Confirm"
        >
          {(
            <FormControlLabel
              control={
                <Checkbox
                  checked={removeFromTitles}
                  onChange={e => setRemoveFromTitles(e.target.checked)}
                  color="primary"
                />
              }
              label="Check if you also want to remove it from Title's page"
            />
          ) as any}
        </ConfirmModal>
        <ActionDivider />
        {!currentVehicle.charge_completed && currentVehicle.awarded_user?.status !== USER_STATUSES.DISABLED && (
          <Fragment>
            <StyledText>The buyer updated his/her payment method. You may retry charging now.</StyledText>
            <StyledButton variant="contained" color="primary" onClick={toggleChargeBuyerModal}>
              charge buyer
            </StyledButton>
            <ActionDivider />
            <ConfirmModal
              submitText={'Confirm'}
              title="Are your sure you want to retry charging?"
              handleSubmit={chargeBuyer}
              modalId={chargeModalTitle}
            />
          </Fragment>
        )}
        <StyledDescription>
          This vehicle was awarded to <StyledText>{currentVehicle.awarded_user?.dealership_name}</StyledText> for the
          amount of{' '}
          <StyledText>
            <NumberFormat displayType="text" prefix="$" value={currentVehicle.vehicle_price} thousandSeparator />
          </StyledText>{' '}
          on <StyledText>{moment(currentVehicle.date_status).format(DATE_FORMAT)}</StyledText>
        </StyledDescription>

        <ActionDivider />
        <SendOfferModal
          maxAmount={currentVehicle.reserve_price}
          form={`unaward-${currentVehicle.id}`}
          vehicle={currentVehicle}
          type="unaward"
          onSuccess={handleCounterSuccess}
        />
        <UnawardFeeDialog
          open={feeDialogOpen}
          buyerName={currentVehicle.awarded_user?.dealership_name ?? ''}
          sellerName={currentVehicle.user?.dealership_name ?? ''}
          onCancel={() => handleUnaward()}
          onConfirm={(buyerFee, sellerFee) => handleUnaward(buyerFee, sellerFee)}
        />
      </Wrapper>
    </InfoBlock>
  );
};
